/**
 * Control sidebar (SPEC §6). Collapses to a bottom sheet on narrow screens —
 * this gets opened on a phone, at launch, in gloves, in wind.
 */

import { useEffect, useRef, useState } from 'react';
import { CompassDial, compassName } from './CompassDial';
import { Legend } from './Legend';
import { SiteSearch } from './SiteSearch';
import {
  HEIGHT_BANDS,
  QUALITY_SETTINGS,
  currentWind,
  useStore,
  type HeightBand,
  type Quality,
} from '../state/store';
import { MODELS } from '../weather/openMeteo';
import { RASTER_LABELS, RASTER_ORDER, rasterAvailableFor } from './rasterLegend';
import { WatermarkControl, HikeFlyControl } from './TerrainLayerControls';
import { ALL_PROVIDERS } from '../dem/resolveDEM';

export function Sidebar() {
  const s = useStore();
  const set = s.set;
  const setLayers = s.setLayers;
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [copied, setCopied] = useState(false);

  const wind = currentWind(s);
  const liveHour = s.mode === 'live' && s.forecast ? s.forecast.hours[s.forecastHour] : null;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <aside className={`sidebar${s.sidebarOpen ? ' open' : ''}`}>
      <button
        className="sheet-grip"
        onClick={() => set({ sidebarOpen: !s.sidebarOpen })}
        aria-label={s.sidebarOpen ? 'Collapse controls' : 'Expand controls'}
      />

      <header className="sidebar-head">
        <h1>Terrain Airflow</h1>
        <p className="tagline">Wind &amp; turbulence over terrain — a model, not a forecast</p>
      </header>

      <div className="sidebar-scroll">
        <SiteSearch />

        <section className="panel">
          <div className="segmented" role="tablist" aria-label="Wind source">
            <button
              role="tab"
              aria-selected={s.mode === 'manual'}
              className={s.mode === 'manual' ? 'active' : ''}
              onClick={() => set({ mode: 'manual' })}
            >
              Manual
            </button>
            <button
              role="tab"
              aria-selected={s.mode === 'live'}
              className={s.mode === 'live' ? 'active' : ''}
              onClick={() => set({ mode: 'live' })}
            >
              Live forecast
            </button>
          </div>

          {s.mode === 'manual' ? (
            <>
              <CompassDial
                value={s.directionDeg}
                onChange={(deg) => set({ directionDeg: deg })}
              />
              <Field label="Wind speed" value={`${s.speedKmh.toFixed(0)} km/h`}>
                <input
                  type="range"
                  min={0}
                  max={60}
                  step={1}
                  value={s.speedKmh}
                  onChange={(e) => set({ speedKmh: Number(e.target.value) })}
                />
              </Field>
            </>
          ) : (
            <LivePanel />
          )}

          <Field
            label="Stability"
            value={stabilityLabel(s.stability)}
            hint={
              s.mode === 'live' && s.stabilityAuto && liveHour
                ? liveHour.stability.basis
                : 'Stable air flows around terrain; unstable air rides over it.'
            }
          >
            <input
              type="range"
              min={-1}
              max={1}
              step={0.05}
              value={s.stability}
              onChange={(e) => set({ stability: Number(e.target.value), stabilityAuto: false })}
            />
            <div className="range-ends">
              <span>unstable</span>
              <span>stable</span>
            </div>
            {s.mode === 'live' && !s.stabilityAuto && (
              <button className="link-btn" onClick={() => set({ stabilityAuto: true })}>
                Follow forecast again
              </button>
            )}
          </Field>

          <Field label="Height band" value={`${s.heightBand} m above ground`}>
            <div className="chip-row">
              {HEIGHT_BANDS.map((h) => (
                <button
                  key={h}
                  className={`chip${s.heightBand === h ? ' active' : ''}`}
                  onClick={() => set({ heightBand: h as HeightBand })}
                >
                  {h} m
                </button>
              ))}
            </div>
          </Field>
        </section>

        <section className="panel">
          <h3>Layers</h3>
          <Field
            label="Background field"
            value=""
            hint={
              s.activeProvider === 'terrarium'
                ? 'Air character and Terrain turbulence need a finer elevation source; ' +
                  'this view fell back to AWS Terrain Tiles.'
                : undefined
            }
          >
            <div className="chip-row">
              {RASTER_ORDER.map((mode) => {
                const available = rasterAvailableFor(mode, s.activeProvider);
                return (
                  <button
                    key={mode}
                    className={`chip${s.layers.raster === mode ? ' active' : ''}`}
                    disabled={!available}
                    title={
                      available
                        ? undefined
                        : 'Needs a finer elevation source than AWS Terrain Tiles'
                    }
                    onClick={() => available && setLayers({ raster: mode })}
                  >
                    {RASTER_LABELS[mode]}
                  </button>
                );
              })}
            </div>
          </Field>
          {s.layers.raster === 'watermark' && <WatermarkControl />}
          {s.layers.raster === 'hikefly' && <HikeFlyControl />}

          {s.layers.raster !== 'none' && (
            <Field label="Field opacity" value={`${Math.round(s.layers.rasterOpacity * 100)}%`}>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={s.layers.rasterOpacity}
                onChange={(e) => setLayers({ rasterOpacity: Number(e.target.value) })}
              />
            </Field>
          )}

          <Toggle
            label="Particle streamlines"
            checked={s.layers.particles}
            onChange={(v) => setLayers({ particles: v })}
          />
          {s.layers.particles && (
            <Field
              label="Streamline opacity"
              value={`${Math.round(s.layers.particleOpacity * 100)}%`}
            >
              <input
                type="range"
                min={0.1}
                max={1}
                step={0.05}
                value={s.layers.particleOpacity}
                onChange={(e) => setLayers({ particleOpacity: Number(e.target.value) })}
              />
            </Field>
          )}
          {s.layers.particles && (
            <Field
              label="Turbulent motion"
              value={
                s.layers.turbulence === 0
                  ? 'off — mean flow'
                  : `${Math.round(s.layers.turbulence * 100)}%`
              }
              hint="Scatters streamlines in proportion to modelled turbulence intensity. At zero you see the mean field only, which looks smooth everywhere — including inside rotor."
            >
              <input
                type="range"
                min={0}
                max={1.5}
                step={0.05}
                value={s.layers.turbulence}
                onChange={(e) => setLayers({ turbulence: Number(e.target.value) })}
              />
            </Field>
          )}
          <Toggle
            label="Rotor / separation zones"
            checked={s.layers.separation}
            onChange={(v) => setLayers({ separation: v })}
          />
          <Toggle
            label="Lift band contour"
            checked={s.layers.liftBand}
            onChange={(v) => setLayers({ liftBand: v })}
          />
          <Toggle
            label="Hillshade"
            checked={s.layers.hillshade}
            onChange={(v) => setLayers({ hillshade: v })}
          />

          <button
            className={`wide-btn${s.crossSectionMode ? ' active' : ''}`}
            onClick={() =>
              set({
                crossSectionMode: !s.crossSectionMode,
                crossSectionLine: s.crossSectionMode ? s.crossSectionLine : null,
              })
            }
          >
            {s.crossSectionMode ? 'Click two points on the map…' : 'Draw cross-section'}
          </button>
        </section>

        <section className="panel">
          <h3>Legend</h3>
          <Legend
            raster={s.layers.raster}
            diagnostics={s.diagnostics}
            liftThreshold={s.liftThreshold}
            freeStreamKmh={wind.speedMs * 3.6}
          />
        </section>

        <section className="panel">
          <button className="disclosure" onClick={() => setShowAdvanced((v) => !v)}>
            {showAdvanced ? '▾' : '▸'} Model settings
          </button>
          {showAdvanced && (
            <div className="advanced">
              <Field label="Resolution" value={QUALITY_SETTINGS[s.quality].label}>
                <div className="chip-row">
                  {(Object.keys(QUALITY_SETTINGS) as Quality[]).map((q) => (
                    <button
                      key={q}
                      className={`chip${s.quality === q ? ' active' : ''}`}
                      onClick={() => set({ quality: q })}
                    >
                      {QUALITY_SETTINGS[q].label}
                      <small>
                        {QUALITY_SETTINGS[q].gridSize}²×{QUALITY_SETTINGS[q].nz}
                      </small>
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Elevation source" value="">
                <select
                  value={s.demProvider}
                  onChange={(e) =>
                    set({ demProvider: e.target.value as typeof s.demProvider })
                  }
                >
                  <option value="auto">Automatic (best available)</option>
                  {ALL_PROVIDERS.map((p) => (
                    <option key={p.info.id} value={p.info.id}>
                      {p.info.label} (~{p.info.nativeResolution} m)
                    </option>
                  ))}
                </select>
              </Field>


              <Field
                label="Lift band threshold"
                value={`${s.liftThreshold.toFixed(1)} m/s`}
                hint="Typical glider sink rate — the contour marks where modelled climb exceeds it."
              >
                <input
                  type="range"
                  min={0.4}
                  max={3}
                  step={0.1}
                  value={s.liftThreshold}
                  onChange={(e) => set({ liftThreshold: Number(e.target.value) })}
                />
              </Field>

              <Toggle
                label="Layer 2 empirical overlays"
                checked={s.empirical}
                onChange={(v) => set({ empirical: v })}
                hint="Off leaves only the mass-conserving field — no rotor, wave or turbulence estimate."
              />
            </div>
          )}
        </section>

        <section className="panel share">
          <button className="wide-btn" onClick={copyLink}>
            {copied ? 'Link copied' : 'Copy shareable link'}
          </button>
          <p className="share-note">
            Opens on this exact view, wind {Math.round(wind.directionDeg)}° (
            {compassName(wind.directionDeg)}) at {(wind.speedMs * 3.6).toFixed(0)} km/h.
          </p>
        </section>
      </div>
    </aside>
  );
}

function LivePanel() {
  const s = useStore();
  const set = s.set;
  const hours = s.forecast?.hours ?? [];
  const hour = hours[s.forecastHour];

  return (
    <div className="live-panel">
      <Field label="Model" value="">
        <select
          value={s.model}
          onChange={(e) => set({ model: e.target.value as typeof s.model })}
        >
          {MODELS.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
      </Field>

      {s.forecastError && <p className="error-note">{s.forecastError}</p>}

      {hours.length > 0 && hour ? (
        <>
          <Field
            label="Time"
            value={`${new Date(hour.time).toLocaleDateString(undefined, {
              weekday: 'short',
            })} ${hour.label}`}
          >
            <input
              type="range"
              min={0}
              max={hours.length - 1}
              step={1}
              value={s.forecastHour}
              onChange={(e) => set({ forecastHour: Number(e.target.value) })}
            />
          </Field>
          <dl className="readout">
            <div>
              <dt>Wind</dt>
              <dd>
                {(currentWind(s).speedMs * 3.6).toFixed(0)} km/h from{' '}
                {Math.round(currentWind(s).directionDeg)}° (
                {compassName(currentWind(s).directionDeg)})
              </dd>
            </div>
            <div>
              <dt>Boundary layer</dt>
              <dd>{Math.round(hour.blHeight)} m</dd>
            </div>
            <div>
              <dt>CAPE</dt>
              <dd>{Math.round(hour.cape)} J/kg</dd>
            </div>
            <div>
              <dt>Temp (2 m)</dt>
              <dd>{hour.temperature2m.toFixed(1)} °C</dd>
            </div>
          </dl>
        </>
      ) : (
        <p className="muted">Loading forecast…</p>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  hint,
  children,
}: {
  label: string;
  value: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="field">
      <div className="field-head">
        <label>{label}</label>
        {value && <span className="field-value">{value}</span>}
      </div>
      {children}
      {hint && <p className="hint">{hint}</p>}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  const id = useRef(`t${Math.random().toString(36).slice(2, 8)}`).current;
  return (
    <div className="toggle-row">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <label htmlFor={id}>
        {label}
        {hint && <small>{hint}</small>}
      </label>
    </div>
  );
}

function stabilityLabel(v: number): string {
  if (v > 0.6) return 'Strongly stable';
  if (v > 0.2) return 'Stable';
  if (v > -0.2) return 'Neutral';
  if (v > -0.6) return 'Unstable';
  return 'Strongly unstable';
}

/** Keep the sheet from trapping scroll on mobile when it is collapsed. */
export function useLockBodyScroll(locked: boolean) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = locked ? 'hidden' : prev;
    return () => {
      document.body.style.overflow = prev;
    };
  }, [locked]);
}
